import type {
    AnyRecord,
    BaseBreakPointConfigProtocol,
    BlockRuntimeContext,
    ButtonBlockAbstract,
    ContainerBlockAbstract,
    ContainerRuntimeState,
    ContainerViewChildren,
    FilterFormType,
    FloatBlockAbstract,
    FormContainerBlockAbstract,
    IconBlockAbstract,
    PageAbstract,
    PageBlockContextIndex,
    Sorter,
    TextBlockAbstract,
    ViewBlockAbstract
} from '@lighthouse/core'
import { type BlockAbstract, BlockType, ChartType } from '@lighthouse/core'
import { findParentScroller } from '@lighthouse/tools'
import { isDraft } from 'immer'

import type { NodeIdWithScope } from '../components'
import { DOM_DATA_NODE_NAME, DOM_DATA_NODE_SCOPE, findNodeDom } from '../components'
import { CONTAINER_PANEL_ACTION_TYPES, PAGE_SCROLL_PARENT_CONTENT } from '../constants'

export type ScrollToAnchorOptions = {
    offset?: { x?: number; y?: number }
    behavior?: ScrollBehavior
}

/**
 * 获取（非）同步组件下的block id
 * @param id block的id
 * @param scope 同步组件的scope（可选）
 * @returns 拼接了scope的id，如果没有scope则返回原id
 */
export function getWithScopeId(id: string, scope?: string) {
    return scope ? `${scope}@${id}` : id
}

export function getWithRecordId(id: string, recordId?: string) {
    return recordId ? `${recordId}@${id}` : id
}

export function getWithItemId(id: string, itemId?: string) {
    return itemId ? `${id}@${itemId}` : id
}

export function getWithScopeIdAndRecordIdAndColumnId(id: string, scope?: string, recordId?: string, columnId?: string) {
    const idWithScope = getWithScopeId(id, scope)
    const idWithScopeAndRecordId = getWithRecordId(idWithScope, recordId)
    return columnId ? `${idWithScopeAndRecordId}@${columnId}` : idWithScopeAndRecordId
}

/** 控制动作监听键。嵌套实例用 `/recordId`，和容器、字段一致。 */
export function getBlockListenerKey(id: string, scope?: string, recordId?: string) {
    const base = getWithScopeId(id, scope)
    return recordId ? `${base}/${recordId}` : base
}

export function isCustomViewBlock(block: BlockAbstract): block is ViewBlockAbstract {
    return block.type === BlockType.view && block.config.viewType === 'custom'
}

export function isContainerBlock(block: BlockAbstract): block is ContainerBlockAbstract {
    return block.type === BlockType.container
}

export function isContainerPanel(block: BlockAbstract | ContainerViewChildren): block is ContainerViewChildren {
    return !('type' in block)
}

export function isFormContainerBlock(block: BlockAbstract): block is FormContainerBlockAbstract {
    return block.type === BlockType.formContainer
}

export function isFloatBoxBlock(block: BlockAbstract): block is FloatBlockAbstract {
    return block.type === BlockType.floatBox
}

export function isIconBlock(block: BlockAbstract): block is IconBlockAbstract {
    return block.type === BlockType.icon
}

export function isButtonBlock(block: BlockAbstract): block is ButtonBlockAbstract {
    return block.type === BlockType.button
}

export function isTextBlock(block: BlockAbstract): block is TextBlockAbstract {
    return block.type === BlockType.text
}

export function hasChildrenBlock(
    block: BlockAbstract
): block is
    | ViewBlockAbstract
    | ContainerBlockAbstract
    | FormContainerBlockAbstract
    | FloatBlockAbstract
    | IconBlockAbstract
    | TextBlockAbstract
    | ButtonBlockAbstract {
    return (
        isCustomViewBlock(block) ||
        isContainerBlock(block) ||
        isFormContainerBlock(block) ||
        isFloatBoxBlock(block) ||
        isIconBlock(block) ||
        isButtonBlock(block) ||
        isTextBlock(block)
    )
}

/** 不可在视图中的block */
const DISALLOWED_BLOCKS_IN_VIEW_BLOCK = new Set<BlockType>([BlockType.view, BlockType.chart])

/** 可放入到block的直接子级中 */
export function canBePlacedIntoDirectBlockChild(targetBlock: BlockAbstract, anotherBlock: BlockAbstract) {
    if (isContainerBlock(targetBlock) || isFormContainerBlock(targetBlock) || isFloatBoxBlock(targetBlock)) {
        return true
    }

    if (isCustomViewBlock(targetBlock) && !DISALLOWED_BLOCKS_IN_VIEW_BLOCK.has(anotherBlock.type)) {
        return true
    }

    return false
}

export type PureBlockAbstract = Omit<BlockAbstract, 'children'>
/** 获取block的配置信息 */
export function getPureBlockProperty(block: BlockAbstract): PureBlockAbstract {
    if (hasChildrenBlock(block)) {
        const { children, ...rest } = block
        return rest
    }
    //
    return block
}

type GetBlockDataSourceIdResult = {
    dsId?: string
    filter?: FilterFormType
    sorts?: Sorter[]
}
export const getBlockDataSourceId = (block: BlockAbstract, pointer: string | undefined): GetBlockDataSourceIdResult => {
    switch (block.type) {
        case BlockType.chart: {
            if (block.config.chartType === ChartType.indicator) {
                return { dsId: undefined }
            }
            return { dsId: block.config.pointer, filter: block.config.ruleFilter }
        }
        case BlockType.view: {
            return { dsId: block.config.pointer, filter: block.config.filter, sorts: block.config.sorts }
        }
        case BlockType.formContainer: {
            return { dsId: block.config.pointer || pointer }
        }
        case BlockType.fieldGroup:
        case BlockType.field: {
            return { dsId: pointer }
        }
        default: {
            return { dsId: pointer }
        }
    }
}

/**
 * 检查是否是自定义视图的后代
 *
 * @export
 * @param {string} target
 * @param {BlockAbstract[]} blocks
 * @returns {boolean}
 */
export function isCustomChildren(targetId: string, blocks: BlockAbstract[]) {
    function findTarget(id: string, tree: BlockAbstract[]) {
        for (const child of tree) {
            if (child.id === id) {
                return true
            }

            const children = getBlockChildren(child)
            if (children) {
                const res = findTarget(id, children)
                if (res) {
                    return true
                }
            }
        }

        return false
    }

    function find(tree: BlockAbstract[]): boolean {
        for (const child of tree) {
            if (isCustomViewBlock(child) && child.children) {
                const res = findTarget(targetId, child.children)
                if (res) {
                    return true
                }
            }

            const children = getBlockChildren(child)
            if (children) {
                const res = find(children)
                if (res) {
                    return true
                }
            }
        }

        return false
    }

    return find(blocks)
}

/**
 * 检查是否是表单容器的后代
 *
 * @export
 * @param {string} target
 * @param {BlockAbstract[]} blocks
 * @returns {boolean}
 */
export function isFormContainerChildren(targetId: string, blocks: BlockAbstract[]) {
    function findTarget(id: string, tree: BlockAbstract[]) {
        for (const child of tree) {
            if (child.id === id) {
                return true
            }

            const children = getBlockChildren(child)
            if (children) {
                const res = findTarget(id, children)
                if (res) {
                    return true
                }
            }
        }

        return false
    }

    function find(tree: BlockAbstract[]): boolean {
        for (const child of tree) {
            if (isFormContainerBlock(child) && child.children) {
                const res = findTarget(targetId, child.children)
                if (res) {
                    return true
                }
            }

            const children = getBlockChildren(child)
            if (children) {
                const res = find(children)
                if (res) {
                    return true
                }
            }
        }

        return false
    }

    return find(blocks)
}

type GetCurrentChildrenOption = {
    /**
     * 获取容器运行时状态的函数
     * @param containerId - 容器的唯一标识符
     *   - 普通容器：使用 block.id
     *   - 同步组件主实例 (isMasterSynchronous)：使用 scope（实例 ID）
     *   - 同步组件叶子节点 (isLeafSynchronous)：使用 `${scope}@${block.id}`
     * @returns 容器的运行时状态，如果不存在则返回 undefined
     */
    getContainerState?: (containerId: string) => ContainerRuntimeState | undefined
    /**
     * 容器的唯一标识符（覆盖默认的 block.id）
     */
    uniqueContainerId?: string
    /**
     * 显式指定当前面板ID（优先级高于 runtime state）
     */
    currentViewId?: string
}
/** 获取当前的block children */
export function getCurrentBlockChildren(block: BlockAbstract, option?: GetCurrentChildrenOption) {
    const { getContainerState, uniqueContainerId, currentViewId } = option ?? {}
    if (hasChildrenBlock(block)) {
        if (isContainerBlock(block)) {
            const containerId = uniqueContainerId ?? block.id
            const containerRuntimeState = getContainerState?.(containerId)

            const resolvedViewId = currentViewId || containerRuntimeState?.currentView || block.children?.[0]?.id
            const view = block.children.find(item => item.id === resolvedViewId)
            if (view) {
                return view.children
            }
            return
        }
        return block.children
    }
}

/** 获取block children，会获取容器下的所有view的children */
export function getBlockChildren(block: BlockAbstract): BlockAbstract[] | undefined {
    if (hasChildrenBlock(block)) {
        if (block.type === BlockType.container) {
            return block.children.reduce<BlockAbstract[]>((total, curr) => [...total, ...curr.children], [])
        }
        return block.children
    }
}

/** 递归查找block */
export function findBlockById<T extends BlockAbstract = BlockAbstract>(id: string, blocks: BlockAbstract[]): T | undefined {
    const recursion = (tree: BlockAbstract[]): undefined | T => {
        for (const block of tree) {
            if (block.id === id) {
                return block as T
            }

            const children = getBlockChildren(block)
            if (children) {
                const res = recursion(children)
                if (res) {
                    return res
                }
            }
        }
    }

    return recursion(blocks)
}

export function filterBlock(blocks: BlockAbstract[], filter: (val: BlockAbstract) => boolean): PureBlockAbstract[] {
    return blocks.reduce<PureBlockAbstract[]>((prev, cur) => {
        const isFilter = filter(cur)
        if (isFilter) {
            const block = getPureBlockProperty(cur)
            prev.push(block)
        }
        const children = getBlockChildren(cur)
        if (children) {
            prev = [...prev, ...filterBlock(children, filter)]
        }
        return prev
    }, [])
}

export function filterSyncBlock(blocks: BlockAbstract[], filter: (val: BlockAbstract) => boolean): PureBlockAbstract[] {
    return blocks.reduce<PureBlockAbstract[]>((prev, cur) => {
        const isFilter = filter(cur)
        if (isFilter) {
            const block = getPureBlockProperty(cur)
            prev.push(block)
        }
        const children = getBlockChildren(cur)
        if (children) {
            prev = [...prev, ...filterBlock(children, filter)]
        }
        return prev
    }, [])
}

/** 深度遍历查找父级block */
export function findParentBlockById<T extends BlockAbstract = BlockAbstract>(id: string, blocks: BlockAbstract[]): T | undefined {
    let parent: BlockAbstract | undefined

    const recursion = (tree: BlockAbstract[]) => {
        for (const node of tree) {
            if (parent) {
                return
            }

            if (node.id === id) {
                return true
            }

            const children = getBlockChildren(node)
            if (children) {
                const res = recursion(children)
                if (res) {
                    if (!parent) {
                        parent = node
                    }

                    return true
                }
            }
        }
    }

    recursion(blocks)

    return parent as T | undefined
}

/**
 * 根据scope查找父级Block
 * **注意！！！**，如果父级是同步组件实例，则返回的是同步组件树中的block信息，同步block的id和页面上的block的id不同
 * @param params
 * @param blocks
 * @returns
 */
export function findParentBlockWithScopeById<T extends BlockAbstract = BlockAbstract>(
    params: NodeIdWithScope,
    blocks: BlockAbstract[],
    syncComponents: BlockAbstract[]
): T | undefined {
    if (params.scope) {
        return findParentBlockById(params.id, syncComponents)
    }

    return findParentBlockById(params.id, blocks)
}

type FindParentBlockOptions = {
    id: string
    blocks: BlockAbstract[]
    syncComponents?: ContainerBlockAbstract[]
    filter: (block: BlockAbstract) => boolean
    /** 获取容器运行时状态的函数 */
    getContainerState?: (containerId: string) => ContainerRuntimeState | undefined
}

/** 查找父级block */
export const findParentBlockByType = <T extends BlockAbstract>({
    id,
    blocks,
    getContainerState,
    syncComponents,
    filter
}: FindParentBlockOptions): T | undefined => {
    let parentBlock: T | undefined

    const recursion = (tree: BlockAbstract[], scope?: string): boolean => {
        for (const node of tree) {
            if (parentBlock) {
                return false
            }
            if (node.id === id) {
                return true
            }

            const actualBlock = findNormalOrSyncBlock({ id: node.id, scope }, blocks, syncComponents || [], false)
            const uniqueContainerId = actualBlock?.isMasterSynchronous
                ? node.id
                : actualBlock?.isLeafSynchronous
                ? `${scope}@${node.id}`
                : node.id
            const children = actualBlock ? getCurrentBlockChildren(actualBlock, { getContainerState, uniqueContainerId }) : []

            if (children) {
                const found = recursion(children, actualBlock?.isMasterSynchronous ? node.id : scope)
                if (found) {
                    if (filter(node)) {
                        parentBlock = node as T
                        return false
                    }
                    return true
                }
            }
        }
        return false
    }
    recursion(blocks)
    return parentBlock
}

/** 查找所有父级block */
export const findAllParentBlocksByType = ({
    id,
    blocks,
    getContainerState,
    syncComponents,
    filter
}: FindParentBlockOptions): BlockAbstract[] => {
    const parentBlocks: BlockAbstract[] = []

    const recursion = (tree: BlockAbstract[], scope?: string): boolean => {
        for (const node of tree) {
            if (node.id === id) {
                return true
            }
            const actualBlock = findNormalOrSyncBlock({ id: node.id, scope }, blocks, syncComponents || [], false)
            const uniqueContainerId = actualBlock?.isMasterSynchronous
                ? node.id
                : actualBlock?.isLeafSynchronous
                ? `${scope}@${node.id}`
                : node.id
            const children = actualBlock ? getCurrentBlockChildren(actualBlock, { getContainerState, uniqueContainerId }) : []

            if (children) {
                const found = recursion(children, actualBlock?.isMasterSynchronous ? node.id : scope)
                if (found) {
                    if (filter(node)) {
                        parentBlocks.push(node)
                        return true
                    }
                    return true
                }
            }
        }
        return false
    }

    recursion(blocks)

    return parentBlocks
}

/**
 * 查找普通block或者同步block
 * 如果在同步组件中找到了则返回
 * 如果在页面block中找到了则判断是否有同步标识，如果有则根据同步标识id在同步组件中找，否则直接返回结果
 * @param node
 * @param pageBlocks
 * @param syncComponents
 * @returns
 */
export function findNormalOrSyncBlock(
    node: NodeIdWithScope,
    pageBlocks: BlockAbstract[],
    syncComponents: BlockAbstract[],
    recursion = true
): BlockAbstract | undefined {
    if (node.scope) {
        return findBlockById(node.id, syncComponents)
    }

    const normalBlock = findBlockById(node.id, pageBlocks)
    if (normalBlock?.synchronousId) {
        return findBlockById(normalBlock.synchronousId, syncComponents)
    }

    if (!normalBlock) {
        return
    }

    if (recursion) {
        if (isContainerBlock(normalBlock)) {
            const newChildren = normalBlock.children.map(view => ({
                ...view,
                children: view.children
                    .map(item => findNormalOrSyncBlock({ id: item.id, scope: node.scope }, pageBlocks, syncComponents))
                    .filter(Boolean) as BlockAbstract[]
            }))

            if (isDraft(normalBlock)) {
                normalBlock.children = newChildren
            } else {
                return {
                    ...normalBlock,
                    children: newChildren
                }
            }
        }

        if (hasChildrenBlock(normalBlock) && !isContainerBlock(normalBlock)) {
            const newChildren = normalBlock.children
                .map(item => findNormalOrSyncBlock({ id: item.id, scope: node.scope }, pageBlocks, syncComponents))
                .filter(Boolean) as BlockAbstract[]

            if (isDraft(normalBlock)) {
                normalBlock.children = newChildren
            } else {
                return {
                    ...normalBlock,
                    children: newChildren
                }
            }
        }
    }

    return normalBlock
}

/**
 * 创建一个带缓存的 findNormalOrSyncBlock 辅助函数
 * 适用于需要多次查找的场景（如批量处理）
 */
export function createBlockFinder(pageBlocks: BlockAbstract[], syncComponents: BlockAbstract[]) {
    // 构建 ID -> Block 的 Map 缓存，避免重复树遍历
    const pageBlocksMap = new Map<string, BlockAbstract>()
    const syncComponentsMap = new Map<string, BlockAbstract>()

    const buildMap = (blocks: BlockAbstract[], map: Map<string, BlockAbstract>) => {
        const traverse = (block: BlockAbstract) => {
            map.set(block.id, block)
            const children = getBlockChildren(block)
            if (children) {
                children.forEach(traverse)
            }
        }
        blocks.forEach(traverse)
    }

    buildMap(pageBlocks, pageBlocksMap)
    buildMap(syncComponents, syncComponentsMap)

    /**
     * 递归处理 block 的子节点，将同步组件实例替换为定义。
     * find 到子块后：若该子块是通过页面块 synchronousId 解析出的同步组件根，则下一次递归传入的 scope 更新为该页面块 id，以便子级从 syncComponentsMap 查找。
     */
    const processChildrenRecursively = (block: BlockAbstract, scope?: string): BlockAbstract => {
        const resolveNextScope = (childId: string): string | undefined =>
            scope ? scope : pageBlocksMap.get(childId)?.synchronousId ? childId : undefined

        if (isContainerBlock(block)) {
            const newChildren = block.children.map(view => ({
                ...view,
                children: view.children
                    .map(item => {
                        const childBlock = internalFind({ id: item.id, scope })
                        return childBlock ? processChildrenRecursively(childBlock, resolveNextScope(item.id)) : undefined
                    })
                    .filter(Boolean) as BlockAbstract[]
            }))

            if (isDraft(block)) {
                block.children = newChildren
                return block
            }

            return {
                ...block,
                children: newChildren
            }
        }

        if (hasChildrenBlock(block)) {
            const newChildren = block.children
                .map(item => {
                    const childBlock = internalFind({ id: item.id, scope })
                    return childBlock ? processChildrenRecursively(childBlock, resolveNextScope(item.id)) : undefined
                })
                .filter(Boolean) as BlockAbstract[]

            if (isDraft(block)) {
                block.children = newChildren
                return block
            }

            return {
                ...block,
                children: newChildren
            }
        }

        return block
    }

    /**
     * 内部查找函数，不处理递归
     */
    const internalFind = (node: NodeIdWithScope): BlockAbstract | undefined => {
        if (node.scope) {
            return syncComponentsMap.get(node.id)
        }

        const normalBlock = pageBlocksMap.get(node.id)
        if (normalBlock?.synchronousId) {
            return syncComponentsMap.get(normalBlock.synchronousId)
        }

        return normalBlock
    }

    return {
        /**
         * 查找 block（不递归处理子节点）
         * 等价于 findNormalOrSyncBlock(node, pageBlocks, syncComponents, false)
         */
        find: internalFind,

        /**
         * 查找 block 并递归处理所有子节点
         * 等价于 findNormalOrSyncBlock(node, pageBlocks, syncComponents, true)
         */
        findWithChildren: (node: NodeIdWithScope): BlockAbstract | undefined => {
            const block = internalFind(node)
            if (!block) {
                return undefined
            }
            return processChildrenRecursively(block, node.scope)
        },

        pageBlocksMap,
        syncComponentsMap
    }
}

/** 递归清除同步block标识 */
export function deepClearSyncBlock<T extends BlockAbstract>(block: T) {
    block.synchronousId = ''
    block.isLeafSynchronous = undefined
    block.isMasterSynchronous = undefined

    if (hasChildrenBlock(block)) {
        if (isContainerBlock(block)) {
            block.children.forEach(view => {
                view.children.forEach(deepClearSyncBlock)
            })
            return
        }
        block.children.forEach(deepClearSyncBlock)
    }
}

/** block是否包含特定的组件 */
export function blockHasSomeChildren(recipe: (block: BlockAbstract) => boolean) {
    function recursion(block: BlockAbstract): boolean {
        if (recipe(block)) {
            return true
        }

        const children = getBlockChildren(block)
        if (!children) {
            return false
        }

        for (const child of children) {
            const res = recursion(child)
            if (res) {
                return res
            }
        }

        return false
    }

    return recursion
}

/**
 * 滚动元素到可见区域，支持 offset 和 behavior
 * offset: x 负左正右, y 负下正上
 */
export function scrollElementIntoView(element: HTMLElement, options?: ScrollToAnchorOptions): void {
    const { offset, behavior = 'instant' } = options || {}

    // offset: y 负下正上，x 负左正右
    // scroll-margin 以原生方式让 scrollIntoView 预留间距，同时支持 smooth/instant 两种行为
    const scrollMarginTop = 60 + (offset?.y ?? 0)
    const scrollMarginLeft = offset?.x ?? 0

    requestAnimationFrame(() => {
        const prevTop = element.style.scrollMarginTop
        const prevLeft = element.style.scrollMarginLeft

        element.style.scrollMarginTop = `${scrollMarginTop}px`
        if (scrollMarginLeft !== 0) element.style.scrollMarginLeft = `${scrollMarginLeft}px`

        element.scrollIntoView({ behavior: behavior, block: 'start', inline: 'nearest' })

        // scroll-margin 只在 scrollIntoView 调用时被读取，浏览器已计算好目标位置
        // 之后立即恢复不会影响正在进行的滚动动画（instant 或 smooth 均安全）
        element.style.scrollMarginTop = prevTop
        element.style.scrollMarginLeft = prevLeft
    })
}

/**
 * 滚动到某个节点
 * @param id block id (data-layout-node)
 * @param scope 可选 scope
 * @param options offset: x 负左正右, y 负下正上; behavior 默认 instant
 */
export function scroll2FlowNode(id: string, scope?: string, options?: ScrollToAnchorOptions): void {
    const element = findNodeDom({ id, scope })
    if (!element) return
    scrollElementIntoView(element, {
        offset: options?.offset,
        behavior: options?.behavior ?? 'instant'
    })
}

/**
 * 滚动到视图中指定的某个元素
 * @param viewId
 * @param index
 * @param scrollIntoViewOptions
 */
export function scrollInViewBlockElement(node: NodeIdWithScope, scrollIntoViewOptions?: ScrollIntoViewOptions) {
    const targetElement = document.querySelector(
        `${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${node.id}"]`
    )
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            document
                .querySelector(`${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${node.id}"]`)
                ?.scrollIntoView({ behavior: 'smooth', ...scrollIntoViewOptions })
        })
    })
}

/**
 * 滚动到视图中指定的某个元素
 * @param viewId
 * @param index
 * @param scrollIntoViewOptions
 */
export function scroll2ViewItem(node: NodeIdWithScope, index: number, scrollIntoViewOptions?: ScrollIntoViewOptions) {
    const targetElement = document.querySelector(
        `${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${node.id}"] div[data-view-item="${index}"]`
    )
    requestAnimationFrame(() => {
        document
            .querySelector(
                `${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${
                    node.id
                }"] div[data-view-item="${index}"]`
            )
            ?.scrollIntoView({ behavior: 'smooth', ...scrollIntoViewOptions })
    })
}

/**
 * 滚动到视图指定位置，左或右，到底
 * @param dom
 * @param scrollOptions
 */
export function scroll2EndLeftOrRight(dom: HTMLElement, scrollOptions?: ScrollToOptions) {
    requestAnimationFrame(() => {
        dom?.scrollTo(scrollOptions)
    })
}

/**
 * 滚动到视图中指定 recordId 的记录
 * @param node 视图节点（包含 id 和 scope）
 * @param recordId 记录 ID
 * @param scrollIntoViewOptions 滚动选项
 */
export function scrollToRecord(node: NodeIdWithScope, recordId: string, scrollIntoViewOptions?: ScrollIntoViewOptions) {
    requestAnimationFrame(() => {
        const selector = `${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${
            node.id
        }"] [data-record-id="${recordId}"]`
        const element = document.querySelector(selector)

        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest', ...scrollIntoViewOptions })
        }
    })
}

const merge = (target: AnyRecord, source: AnyRecord, path: string[]) => {
    let sourceValue = source

    // 遍历路径
    for (const part of path) {
        if (sourceValue !== undefined && typeof sourceValue === 'object') {
            sourceValue = sourceValue?.[part]
        } else {
            break
        }
    }
    // if(!sourceValue){
    //     return
    // }
    // 在目标对象中设置更新的值
    let targetRef = target
    for (let i = 0; i < path.length - 1; i++) {
        const part = path[i]
        if (!targetRef?.[part] && sourceValue !== undefined) {
            targetRef[part] = {} // 创建嵌套对象
        }
        targetRef = targetRef?.[part]
    }
    if (typeof targetRef === 'object') {
        targetRef[path[path.length - 1]] = sourceValue
    }
}

export const deepMerge = (target: AnyRecord, source: AnyRecord, breakKeys: string[] = [], currentPath = '') => {
    const mergeObj = JSON.parse(JSON.stringify(target))
    const sourceObj = JSON.parse(JSON.stringify(source))
    for (const path of breakKeys) {
        const pathParts = path.split('.')
        merge(mergeObj, sourceObj, pathParts)
    }
    return mergeObj
}

export function mergeSingleBreakPointConfigure<T extends BaseBreakPointConfigProtocol>(base: T, breakPoint: Partial<T>): T {
    const target = {
        ...base,
        breakKeys: breakPoint.breakKeys
    }
    return deepMerge(target, breakPoint, ['id', 'name', ...(breakPoint.breakKeys || [])]) as T
}

export function mergeBreakPointConfigure<T extends BaseBreakPointConfigProtocol>(
    base: T,
    breakPoints: Partial<T>[],
    id: string
): T | undefined {
    const otherBreakPoint = breakPoints.find(item => item.id === id)
    if (!otherBreakPoint) {
        return
    }
    return mergeSingleBreakPointConfigure(base, otherBreakPoint)
}

export function getBreakPointConfigure<T extends BaseBreakPointConfigProtocol>(
    previewId: string,
    mainBreakPoint: string,
    base: T,
    breakPoints: Partial<T>[]
) {
    if (previewId === mainBreakPoint && base.id === previewId) {
        return base
    }
    const alternative = { ...base, id: previewId, breakKeys: [] }
    return mergeBreakPointConfigure(base, breakPoints, previewId) || alternative
}

export function getBlockWithMergedBreakPoint<T extends BlockAbstract>(previewId: string, mainBreakPoint: string, data: T) {
    const { breakPoint, breakPoints } = data.config
    if (previewId === mainBreakPoint && breakPoint.id === previewId) {
        return data
    }
    const mergeBreakPoint = mergeBreakPointConfigure(breakPoint, breakPoints, previewId)
    return {
        ...data,
        config: {
            ...data.config,
            breakPoint: mergeBreakPoint || breakPoint
        }
    }
}

export function getPageWithMergedBreakPoint<T extends PageAbstract>(previewId: string, mainBreakPoint: string, data: T) {
    const { breakPoint, breakPoints, ...rest } = data

    if (!breakPoint) {
        return data
    }
    if (previewId === mainBreakPoint && breakPoint.id === previewId) {
        return data
    }
    const mergeBreakPoint = mergeBreakPointConfigure(breakPoint, breakPoints, previewId)
    // const currentBreakPoint = find(item => item.id === previewId, mergeBreakPoints || [])
    return {
        ...data,
        breakPoint: mergeBreakPoint || breakPoint
    }
}

export const findMissingKeysDeep = (data: any, maxLevel: number | undefined, parentKey = '', level = 1): string[] => {
    let missingKeys: string[] = []
    if (typeof data !== 'object' || Array.isArray(data)) {
        return missingKeys
    }
    if (maxLevel && maxLevel < level) {
        return [parentKey]
    }
    Object.keys(data).forEach(key => {
        const currentKey = parentKey ? `${parentKey}.${key}` : key
        if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
            const nestedMissingKeys = findMissingKeysDeep(data[key], maxLevel, currentKey, level + 1)
            missingKeys = [...missingKeys, ...nestedMissingKeys]
        } else {
            missingKeys.push(currentKey)
        }
    })
    return missingKeys
}

/** 查找父级同步容器 */
export const findParentSyncContainerBlock = ({ id, blocks, getContainerState }: Omit<FindParentBlockOptions, 'filter'>) => {
    let containerBlock: ContainerBlockAbstract | undefined

    const recursion = (tree: BlockAbstract[]) => {
        for (const node of tree) {
            if (containerBlock) {
                return
            }

            if (node.id === id) {
                return true
            }

            const children = getCurrentBlockChildren(node, { getContainerState })

            if (children) {
                const res = recursion(children)
                if (res) {
                    if (node.type === BlockType.container && node.isMasterSynchronous) {
                        containerBlock = node
                        return
                    }
                    return true
                }
            }
        }
    }

    recursion(blocks)

    return containerBlock
}

export function transformOriginBlock<T extends BlockAbstract = BlockAbstract>(blocks: T[], previewId: string, mainBreakPoint: string): T[] {
    return blocks.map<T>(block => {
        const newBlock = getBlockWithMergedBreakPoint(previewId, mainBreakPoint, block)
        if (hasChildrenBlock(newBlock)) {
            if (isContainerBlock(newBlock) && newBlock.children) {
                return {
                    ...newBlock,
                    children: newBlock.children.map(item => ({
                        ...item,
                        children: transformOriginBlock(item.children, previewId, mainBreakPoint)
                    }))
                }
            }
            if (newBlock.children && newBlock.children.length > 0) {
                return {
                    ...newBlock,
                    children: transformOriginBlock(newBlock.children as BlockAbstract[], previewId, mainBreakPoint)
                }
            }
        }
        return newBlock
    })
}

/**
 * 计算目标面板ID
 * @param switchPanelId - 切换类型：具体ID、'nextPanel'、'prevPanel'、'nthPanel'（第几个，需配合 nthPanel）
 * @param currentPanelId - 当前面板ID
 * @param viewList - 视图列表
 * @param nthPanel - switchPanelId 为 'nthPanel' 时有效：1-based 序号；未填、非有限数或不在 [1, viewList.length] 内则返回 undefined（不切换）
 * @returns 目标面板ID
 */
export const calculateNextPanelId = (
    switchPanelId: string,
    currentPanelId: string | undefined,
    viewList: { id: string }[],
    nthPanel?: number
): string | undefined => {
    if (switchPanelId === 'nthPanel') {
        if (viewList.length === 0) {
            return
        }
        if (nthPanel == null || typeof nthPanel !== 'number' || !Number.isFinite(nthPanel)) {
            return
        }
        const indexOneBased = Math.floor(nthPanel)
        if (indexOneBased < 1 || indexOneBased > viewList.length) {
            return
        }
        return viewList[indexOneBased - 1]?.id
    }

    if (!CONTAINER_PANEL_ACTION_TYPES.includes(switchPanelId)) {
        return switchPanelId
    }

    if (viewList.length === 0) {
        return
    }

    const currentIndex = viewList.findIndex(v => v.id === currentPanelId)
    const offset = switchPanelId === 'nextPanel' ? 1 : -1
    const nextIndex = (currentIndex + offset + viewList.length) % viewList.length

    return viewList[nextIndex].id
}

// ============== Block 上下文索引构建 ==============

/** uniqueKey 分隔符 */
const UNIQUE_KEY_SEPARATOR = '@'

/**
 * 构建 block 的 uniqueKey（用于索引的唯一标识）
 * 格式：scope@blockId 或 blockId（无 scope 时）
 */
export function buildBlockUniqueKey(blockId: string, scope?: string): string {
    return scope ? `${scope}${UNIQUE_KEY_SEPARATOR}${blockId}` : blockId
}

/**
 * 解析 uniqueKey，提取 blockId 和 scope
 */
export function parseBlockUniqueKey(uniqueKey: string): { blockId: string; scope?: string } {
    const separatorIndex = uniqueKey.indexOf(UNIQUE_KEY_SEPARATOR)
    if (separatorIndex === -1) {
        return { blockId: uniqueKey }
    }
    return {
        scope: uniqueKey.slice(0, separatorIndex),
        blockId: uniqueKey.slice(separatorIndex + 1)
    }
}

type BuildPageBlockContextIndexOptions = {
    blocks: BlockAbstract[]
    syncComponents: ContainerBlockAbstract[]
}

/**
 * 构建页面 block 上下文索引
 * 一次 O(n) 遍历，构建完整的上下文信息，供所有消费方使用
 */
export function buildPageBlockContextIndex({ blocks, syncComponents }: BuildPageBlockContextIndexOptions): PageBlockContextIndex {
    const contextMap = new Map<string, BlockRuntimeContext>()
    const blockMap = new Map<string, BlockAbstract>()
    const parentMap = new Map<string, string>()
    const syncComponentMap = new Map<string, ContainerBlockAbstract>()

    for (const syncComponent of syncComponents) {
        syncComponentMap.set(syncComponent.id, syncComponent)
    }

    function resolveContextBlock(
        block: BlockAbstract,
        scope: string | undefined
    ): {
        block: BlockAbstract
        originBlock?: BlockAbstract
        syncBlock?: BlockAbstract
    } {
        if (!block.synchronousId) {
            if (scope) {
                return { block, syncBlock: block }
            }
            return { block }
        }

        const syncBlock = syncComponentMap.get(block.synchronousId)
        if (!syncBlock) {
            return { block, originBlock: block }
        }

        return {
            block: {
                ...block,
                ...syncBlock,
                id: block.id,
                synchronousId: block.synchronousId
            },
            originBlock: block,
            syncBlock
        }
    }

    function traverse(
        tree: BlockAbstract[],
        scope: string | undefined,
        parentUniqueKey: string | undefined,
        formContainer: FormContainerBlockAbstract | undefined,
        customView: ViewBlockAbstract | undefined,
        container: ContainerBlockAbstract | undefined,
        panelId?: string
    ) {
        for (const block of tree) {
            const resolved = resolveContextBlock(block, scope)
            const contextBlock = resolved.block

            // 构建当前 block 的 uniqueKey
            const uniqueKey = buildBlockUniqueKey(block.id, scope)

            // 先存储当前 block 的上下文（使用祖先的容器信息）
            const context: BlockRuntimeContext = {
                block: contextBlock,
                originBlock: resolved.originBlock,
                syncBlock: resolved.syncBlock,
                uniqueKey,
                parentUniqueKey,
                formContainer,
                customView,
                container,
                scope,
                panelId
            }
            contextMap.set(uniqueKey, context)
            blockMap.set(uniqueKey, contextBlock)
            if (parentUniqueKey) {
                parentMap.set(uniqueKey, parentUniqueKey)
            }

            // 再更新容器引用，传递给子节点
            let currentFormContainer = formContainer
            let currentCustomView = customView
            let currentContainer = container

            if (contextBlock.type === BlockType.formContainer) {
                currentFormContainer = contextBlock
            }
            if (contextBlock.type === BlockType.view && contextBlock.config?.viewType === 'custom') {
                currentCustomView = contextBlock
            }
            if (contextBlock.type === BlockType.container) {
                currentContainer = contextBlock
            }

            // 计算下一层的 scope
            const nextScope = contextBlock.isMasterSynchronous ? block.id : scope

            // 多面板容器：遍历所有面板，使索引包含全部 block，派生 atom 再按 currentView 过滤
            if (isContainerBlock(contextBlock)) {
                if (block.synchronousId) {
                    const syncComponent = syncComponentMap.get(block.synchronousId)
                    if (syncComponent && isContainerBlock(syncComponent) && syncComponent.children?.length) {
                        for (const view of syncComponent.children) {
                            if (view.children?.length) {
                                traverse(
                                    view.children,
                                    block.id,
                                    uniqueKey,
                                    currentFormContainer,
                                    currentCustomView,
                                    currentContainer,
                                    view.id
                                )
                            }
                        }
                        continue
                    }
                }
                for (const view of contextBlock.children) {
                    if (view.children?.length) {
                        traverse(view.children, nextScope, uniqueKey, currentFormContainer, currentCustomView, currentContainer, view.id)
                    }
                }
                continue
            }

            // 非多面板容器：直接递归子节点
            const children = getBlockChildren(contextBlock)
            if (children?.length) {
                traverse(children, nextScope, uniqueKey, currentFormContainer, currentCustomView, currentContainer)
            }
        }
    }

    traverse(blocks, undefined, undefined, undefined, undefined, undefined)
    return { contextMap, blockMap, parentMap }
}

/**
 * 从索引中获取 block 的祖先链（context 信息，含 block、scope、customView 等）
 * @param uniqueKey - block 的 uniqueKey
 * @param index - 页面 block 上下文索引
 * @returns 从根到父的祖先 context 链（不含自身）
 */
export function getBlockAncestors(uniqueKey: string, index: PageBlockContextIndex): BlockRuntimeContext[] {
    const ancestors: BlockRuntimeContext[] = []
    let currentKey = index.parentMap.get(uniqueKey)

    while (currentKey) {
        const context = index.contextMap.get(currentKey)
        if (context) {
            ancestors.unshift(context)
        }
        currentKey = index.parentMap.get(currentKey)
    }

    return ancestors
}

/**
 * 从索引中查找指定类型的祖先 block
 * @param uniqueKey - block 的 uniqueKey
 * @param index - 页面 block 上下文索引（使用 contextMap）
 * @param predicate - 判断函数
 * @returns 第一个匹配的祖先 block，或 undefined
 */
export function findAncestorBlock<T extends BlockAbstract>(
    uniqueKey: string,
    index: PageBlockContextIndex,
    predicate: (block: BlockAbstract) => boolean
): T | undefined {
    let currentKey = index.parentMap.get(uniqueKey)

    while (currentKey) {
        const context = index.contextMap.get(currentKey)
        if (context && predicate(context.block)) {
            return context.block as T
        }
        currentKey = index.parentMap.get(currentKey)
    }

    return undefined
}
